import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import {
    Container,
    Box,
    Button,
    CircularProgress,
    Typography,
    Alert,
    Paper,
    Grid,
    ButtonGroup,
    Skeleton, // Import Skeleton for loading effect
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { createExplorerTheme } from '../../theme/theme';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';
import WithdrawComponent from '../Components/Withdraw.component';
import DepositComponent from '../Components/Deposit.component';
import OdinDetails from '../Components/OdinDetails.component';

// New Metadata Display Component
const MetadataDisplay = ({ meta, theme, loading, btcPrice, t }) => {
    const formatVolumeWithUSD = (btcValue) => {
        if (!btcValue || btcValue === 'N/A') return { btc: t('common.notAvailable'), usd: null };
        const btcAmount = parseFloat(btcValue);
        if (isNaN(btcAmount)) return { btc: t('common.notAvailable'), usd: null };

        const btcFormatted = `${ btcAmount.toFixed(4) } BTC`;
        let usdFormatted = null;

        if (btcPrice) {
            const usdValue = btcAmount * btcPrice;
            usdFormatted = `$${ usdValue.toLocaleString('en-US', { maximumFractionDigits: 0 }) }`;
        }

        return { btc: btcFormatted, usd: usdFormatted };
    };

    const metrics = [
        {
            label: t('explorer.totalTxAllTime'),
            value: meta?.total_transactions_for_all_time,
            color: '#4caf50',
            isVolume: false
        },
        {
            label: t('explorer.totalVolAllTime'),
            value: meta?.total_volume_for_all_time,
            color: theme.palette.primary.main,
            isVolume: true
        },
    ];

    return (
        <Grid container spacing={2} sx={{ height: '100%' }}>
            {metrics.map((metric, index) => (
                <Grid item xs={12} key={index}>
                    <Paper elevation={3} sx={{
                        p: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        textAlign: 'center',
                    }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, fontWeight: 'medium' }}>
                            {metric.label}
                        </Typography>
                        {loading ? (
                            <>
                                <Skeleton variant="text" width={80} height={32} />
                                {metric.isVolume && <Skeleton variant="text" width={60} height={20} sx={{ mt: 0.5 }} />}
                            </>
                        ) : (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: metric.color }}>
                                    {metric.isVolume ? formatVolumeWithUSD(metric.value).btc : (metric.value || t('common.notAvailable'))}
                                </Typography>
                                {metric.isVolume && formatVolumeWithUSD(metric.value).usd && (
                                    <Typography variant="body2" sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                        mt: 0.5
                                    }}>
                                        {formatVolumeWithUSD(metric.value).usd}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Paper>
                </Grid>
            ))}
        </Grid>
    );
};

function App() {
    const { t } = useTranslation();
    const { theme: baseTheme } = useTheme();
    const theme = createExplorerTheme(baseTheme);
    const { price: btcPrice } = useBitcoinPrice();

    // Fixed parameters as per user's request
    const fixedLedgerCanisterId = 'mxzaz-hqaaa-aaaar-qaada-cai';
    const fixedStep = 3600; // Hourly
    const fixedAccountId = 'ztwhb-qiaaa-aaaaj-azw7a-cai';
    const minTimestamp = 1620328530; // Minimum allowed timestamp from API docs

    const [data, setData] = useState([]);
    const [metaData, setMetaData] = useState(null); // State to store metadata
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedRange, setSelectedRange] = useState('1day'); // Default selected range

    const svgRef = useRef();

    // Function to calculate start and end timestamps based on selected range
    const calculateTimestamps = (range) => {
        const now = Math.floor(Date.now() / 1000); // Current Unix timestamp
        let start = now;
        let end = now;

        switch (range) {
            case '1day':
                start = now - 24 * 60 * 60;
                break;
            case '1week':
                start = now - 7 * 24 * 60 * 60;
                break;
            case '1month':
                start = now - 30 * 24 * 60 * 60; // Approximately 30 days
                break;
            case '1year':
                start = now - 365 * 24 * 60 * 60; // Approximately 365 days
                break;
            case 'all':
                start = minTimestamp; // Use the API's minimum timestamp
                break;
            default:
                start = now - 7 * 24 * 60 * 60; // Default to 1 week if somehow invalid
        }
        return { start: Math.max(start, minTimestamp), end }; // Ensure start is not below minTimestamp
    };

    // Function to fetch data from the API
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        setData([]); // Clear previous data
        setMetaData(null); // Clear previous metadata

        const { start, end } = calculateTimestamps(selectedRange);

        // Construct API URL with fixed parameters and calculated start/end
        const apiUrl = `https://icrc-api.internetcomputer.org/api/v1/ledgers/${ fixedLedgerCanisterId }/transaction-volume?start=${ start }&end=${ end }&step=${ fixedStep }&account_id=${ fixedAccountId }`;

        try {
            const response = await fetch(apiUrl);
            if (!response.ok) {
                // Handle HTTP errors
                const errorData = await response.json();
                throw new Error(errorData.detail ? errorData.detail[0].msg : `HTTP error! status: ${ response.status }`);
            }
            const result = await response.json();
            // Ensure data is an array and has expected structure
            if (result && result.data && Array.isArray(result.data)) {
                // Filter out data points where timestamp is 0 or volume/count are not valid numbers
                const cleanedData = result.data.filter(d =>
                    d.timestamp > 0 &&
                    !isNaN(parseFloat(d.volume)) &&
                    !isNaN(parseFloat(d.count))
                );
                setData(cleanedData);
                setMetaData(result.meta || null); // Store metadata if available
            } else {
                setError(t('explorer.unexpectedDataFormat'));
            }
        } catch (e) {
            console.error('Fetch error:', e);
            setError(`${ t('explorer.failedToFetchData') }: ${ e.message }`);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data initially and whenever the selected range changes
    useEffect(() => {
        fetchData();
    }, [selectedRange]);

    // Effect hook to draw the D3 chart when data changes
    useEffect(() => {
        if (data.length === 0) {
            d3.select(svgRef.current).selectAll('*').remove(); // Clear SVG if no data
            return;
        }

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove(); // Clear previous chart elements

        const margin = { top: 20, right: 100, bottom: 60, left: 80 }; // Increased right margin for second Y-axis label
        const width = svgRef.current.parentElement.clientWidth - margin.left - margin.right;
        const height = 400 - margin.top - margin.bottom;

        // Set SVG dimensions and viewbox for responsiveness
        svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .attr('viewBox', `0 0 ${ width + margin.left + margin.right } ${ height + margin.top + margin.bottom }`)
            .attr('preserveAspectRatio', 'xMidYMid meet');

        const g = svg.append('g').attr('transform', `translate(${ margin.left },${ margin.top })`);

        // Parse timestamps and values
        const formattedData = data.map(d => ({
            date: new Date(d.timestamp * 1000), // Convert Unix timestamp to Date object
            volume: parseFloat(d.volume), // Convert volume string to number
            count: parseFloat(d.count),
        }));

        // Define scales
        const x = d3
            .scaleTime()
            .domain(d3.extent(formattedData, (d) => d.date))
            .range([0, width]);

        // Y scale for Volume
        const yVolume = d3
            .scaleLinear()
            .domain([0, d3.max(formattedData, (d) => d.volume) * 1.05]) // Add 5% padding to top
            .range([height, 0]);

        // Y scale for Count
        const yCount = d3
            .scaleLinear()
            .domain([0, d3.max(formattedData, (d) => d.count) * 1.05])
            .range([height, 0]);

        // Define the line generator for Volume
        const lineVolume = d3
            .line()
            .x((d) => x(d.date))
            .y((d) => yVolume(d.volume));

        // Define the line generator for Count
        const lineCount = d3
            .line()
            .x((d) => x(d.date))
            .y((d) => yCount(d.count));

        // Add X axis
        const xAxis = g.append('g')
            .attr('transform', `translate(0,${ height })`)
            .call(d3.axisBottom(x).tickFormat(d3.timeFormat(fixedStep === 3600 ? '%H:%M %b %d' : '%b %d, %Y')));

        xAxis.selectAll('text')
            .attr('transform', 'rotate(-45)')
            .style('text-anchor', 'end')
            .style('fill', theme.palette.text.primary);

        xAxis.selectAll('line')
            .style('stroke', theme.palette.divider);

        xAxis.select('.domain')
            .style('stroke', theme.palette.divider);

        // Add Y axis for Volume (left)
        const yAxisLeft = g.append('g')
            .call(d3.axisLeft(yVolume).tickFormat((d) => {
                if (d >= 1) {
                    return d3.format('.0s')(d);
                } else if (d >= 0.001) {
                    return d3.format('.3f')(d);
                } else {
                    return d3.format('.6f')(d);
                }
            }));

        yAxisLeft.selectAll('text')
            .style('fill', theme.palette.text.primary);

        yAxisLeft.selectAll('line')
            .style('stroke', theme.palette.divider);

        yAxisLeft.select('.domain')
            .style('stroke', theme.palette.divider);

        yAxisLeft.append('text')
            .attr('class', 'y label')
            .attr('text-anchor', 'middle')
            .attr('y', -margin.left + 30)
            .attr('x', -height / 2)
            .attr('transform', 'rotate(-90)')
            .attr('fill', theme.palette.primary.main)
            .style('font-size', '14px') // Set font size for Y-axis label
            .text(t('explorer.transactionVolumeBTC'));

        // Add Y axis for Count (right)
        const yAxisRight = g.append('g')
            .attr('transform', `translate(${ width }, 0)`)
            .call(d3.axisRight(yCount).tickFormat(d3.format('.0s')));

        yAxisRight.selectAll('text')
            .style('fill', theme.palette.text.primary);

        yAxisRight.selectAll('line')
            .style('stroke', theme.palette.divider);

        yAxisRight.select('.domain')
            .style('stroke', theme.palette.divider);

        // Add Y axis label for Count (positioned outside the graph)
        g.append('text')
            .attr('class', 'y label')
            .attr('text-anchor', 'middle')
            .attr('transform', `translate(${ width + margin.right - 40 }, ${ height / 2 }) rotate(90)`) // Adjusted position
            .attr('fill', '#4caf50')
            .style('font-size', '14px') // Set font size for Y-axis label
            .text(t('explorer.transactionCount'));


        // Add X axis label
        g.append('text')
            .attr('class', 'x label')
            .attr('text-anchor', 'middle')
            .attr('x', width / 2)
            .attr('y', height + margin.bottom - 10)
            .attr('fill', 'white')
            .style('font-size', '14px') // Set font size for X-axis label
            .text(t('explorer.date'));

        // Add the line path for Volume (solid line)
        g.append('path')
            .datum(formattedData)
            .attr('fill', 'none')
            .attr('stroke', theme.palette.primary.main)
            .attr('stroke-width', 3)
            .attr('d', lineVolume);

        // Add the line path for Count (green solid line)
        g.append('path')
            .datum(formattedData)
            .attr('fill', 'none')
            .attr('stroke', '#4caf50')
            .attr('stroke-width', 3)
            .attr('d', lineCount);

        // Add circles for data points and tooltips
        const focus = g.append('g').style('display', 'none');

        // Tooltip for Volume
        focus.append('circle').attr('r', 5).attr('fill', theme.palette.primary.main).attr('class', 'focus-volume-circle');
        focus.append('text')
            .attr('x', 10)
            .attr('dy', '.35em')
            .style('font-size', '12px')
            .style('fill', theme.palette.primary.main)
            .attr('class', 'focus-volume-text');

        // Tooltip for Count
        focus.append('circle').attr('r', 5).attr('fill', '#4caf50').attr('class', 'focus-count-circle');
        focus.append('text')
            .attr('x', 10)
            .attr('dy', '1.5em') // Offset for second line
            .style('font-size', '12px')
            .style('fill', '#4caf50')
            .attr('class', 'focus-count-text');


        svg.append('rect')
            .attr('class', 'overlay')
            .attr('width', width)
            .attr('height', height)
            .style('fill', 'none')
            .style('pointer-events', 'all')
            .attr('transform', `translate(${ margin.left },${ margin.top })`)
            .on('mouseover', () => focus.style('display', null))
            .on('mouseout', () => focus.style('display', 'none'))
            .on('mousemove', mousemove);

        function mousemove(event) {
            const bisectDate = d3.bisector(d => d.date).left;
            const x0 = x.invert(d3.pointer(event)[0]);
            const i = bisectDate(formattedData, x0, 1);
            const d0 = formattedData[i - 1];
            const d1 = formattedData[i];
            const d = x0 - d0.date > d1.date - x0 ? d1 : d0;

            // Position volume tooltip with USD conversion
            const volumeBTC = d3.format('.4f')(d.volume);
            let volumeText = `${ t('explorer.vol') }: ${ volumeBTC } BTC`;
            if (btcPrice) {
                const usdValue = d.volume * btcPrice;
                volumeText += ` ($${ d3.format(',.0f')(usdValue) })`;
            }

            focus.select('.focus-volume-circle').attr('transform', `translate(${ x(d.date) },${ yVolume(d.volume) })`);
            focus.select('.focus-volume-text').attr('transform', `translate(${ x(d.date) },${ yVolume(d.volume) })`)
                .text(volumeText);

            // Position count tooltip
            focus.select('.focus-count-circle').attr('transform', `translate(${ x(d.date) },${ yCount(d.count) })`);
            focus.select('.focus-count-text').attr('transform', `translate(${ x(d.date) },${ yCount(d.count) })`)
                .text(`${ t('explorer.count') }: ${ d3.format('.2s')(d.count) }`);
        }

        // Add legend
        const legend = g.append('g')
            .attr('font-family', 'sans-serif')
            .attr('font-size', 10)
            .attr('text-anchor', 'end')
            .selectAll('g')
            .data([t('explorer.transactionVolume'), t('explorer.transactionCount')])
            .enter().append('g')
            .attr('transform', (d, i) => `translate(0,${ i * 20 })`);

        legend.append('rect')
            .attr('x', width - 19)
            .attr('width', 19)
            .attr('height', 19)
            .attr('fill', (d) => {
                if (d === t('explorer.transactionVolume')) return theme.palette.primary.main;
                if (d === t('explorer.transactionCount')) return '#4caf50';
                return 'black'; // Default color
            });

        legend.append('text')
            .attr('x', width - 24)
            .attr('y', 9.5)
            .attr('dy', '0.32em')
            .style('fill', theme.palette.text.primary)
            .text((d) => {
                if (d === t('explorer.transactionVolume')) return t('explorer.transactionVolumeBTC');
                return d;
            });

    }, [data, theme.palette.primary.main, theme.palette.secondary.main]);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Grid container spacing={4} sx={{ mt: 2 }}>
                <Grid item xs={12} md={12}>
                    <OdinDetails />
                </Grid>

                {/* Chart and Time Range Selection Section */}
                <Grid item xs={12} md={12}>
                    <Paper elevation={3} sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 450 }}>
                        {/* Chart Title and Time Range Selection on same row */}
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                {t('explorer.transactionVolumeCountOverTime')}
                            </Typography>

                            <Box sx={{}}>
                                <ButtonGroup fullWidth variant="contained" aria-label="time range selection">
                                    <Button
                                        onClick={() => setSelectedRange('1day')}
                                        color={selectedRange === '1day' ? 'primary' : 'inherit'}
                                    >
                                        1D
                                    </Button>
                                    <Button
                                        onClick={() => setSelectedRange('1week')}
                                        color={selectedRange === '1week' ? 'primary' : 'inherit'}
                                    >
                                        1W
                                    </Button>
                                    <Button
                                        onClick={() => setSelectedRange('1month')}
                                        color={selectedRange === '1month' ? 'primary' : 'inherit'}
                                    >
                                        1M
                                    </Button>
                                    <Button
                                        onClick={() => setSelectedRange('1year')}
                                        color={selectedRange === '1year' ? 'primary' : 'inherit'}
                                    >
                                        1Y
                                    </Button>
                                    <Button
                                        onClick={() => setSelectedRange('all')}
                                        color={selectedRange === 'all' ? 'primary' : 'inherit'}
                                    >
                                        All
                                    </Button>
                                </ButtonGroup>
                            </Box>
                        </Box>

                        {/* Dataset Metadata Row */}
                        {metaData && (
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', gap: 3, mb: 2 }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                        {t('explorer.datasetTransactions')}
                                    </Typography>
                                    {loading ? (
                                        <Skeleton variant="text" width={60} height={24} />
                                    ) : (
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                                            {metaData?.total_transactions_for_dataset || t('common.notAvailable')}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                        {t('explorer.datasetVolume')}
                                    </Typography>
                                    {loading ? (
                                        <>
                                            <Skeleton variant="text" width={80} height={24} />
                                            <Skeleton variant="text" width={60} height={16} sx={{ mt: 0.5 }} />
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                {metaData?.total_volume_for_dataset ?
                                                    `${ parseFloat(metaData.total_volume_for_dataset).toFixed(4) } BTC` :
                                                    t('common.notAvailable')
                                                }
                                            </Typography>
                                            {metaData?.total_volume_for_dataset && btcPrice && (
                                                <Typography variant="body2" sx={{
                                                    color: 'text.secondary',
                                                    fontSize: '0.75rem'
                                                }}>
                                                    ${(parseFloat(metaData.total_volume_for_dataset) * btcPrice).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                                {error}
                            </Alert>
                        )}

                        {/* Conditional rendering for loading skeleton or chart */}
                        {loading && data.length === 0 ? (
                            <Box sx={{ width: '100%', height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Skeleton variant="rectangular" width="90%" height="90%" sx={{ borderRadius: 2 }} />
                            </Box>
                        ) : (
                            <>
                                {!loading && data.length === 0 && !error && (
                                    <Typography variant="h6" color="text.secondary" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                                        {t('explorer.selectTimeRangeToSeeChart')}
                                    </Typography>
                                )}
                                <svg ref={svgRef} style={{ width: '100%', height: 'auto', display: data.length > 0 ? 'block' : 'none' }}></svg>
                            </>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={6}>
                            <DepositComponent />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <WithdrawComponent />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    );
}

export default App;
